import { useDisclosure } from "@mantine/hooks";
import { Dialog, Group, Button, Text } from "@mantine/core";
import { useEffect, useState } from "react";

const DISMISSED_KEY = "pwa-install-dismissed";

function PWAInstaller() {
  const [deferredPrompt, setDeferredPrompt] = useState<Event | null>(null);
  const [opened, { open, close }] = useDisclosure(false);
  const [isDismissed, setIsDismissed] = useState(false);

  // Check if app is already installed
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if user has previously dismissed the dialog
    const dismissed = localStorage.getItem(DISMISSED_KEY) === "true";
    setIsDismissed(dismissed);

    // Handler for beforeinstallprompt
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault(); // Prevents the mini-infobar or auto-prompt
      setDeferredPrompt(e);
      if (!dismissed) {
        open();
      }
    };

    // Handler for appinstalled event
    const handleAppInstalled = () => {
      setIsInstalled(true);
      close();
    };

    // iOS/Safari detection (no beforeinstallprompt on iOS)
    const isIosStandalone =
      // @ts-ignore
      window.navigator.standalone ||
      window.matchMedia("(display-mode: standalone)").matches;
    setIsInstalled(isIosStandalone);

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);

    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt,
      );
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt && "prompt" in deferredPrompt) {
      // Cast for typescript
      const promptEvent = deferredPrompt as any;
      promptEvent.prompt();
      const choiceResult = await promptEvent.userChoice;
      if (choiceResult.outcome === "accepted") {
        setDeferredPrompt(null);
        close();
      }
    }
  };

  const handleNoThanksClick = () => {
    localStorage.setItem(DISMISSED_KEY, "true");
    setIsDismissed(true);
    close();
  };

  // Don't show dialog if PWA is installed or install prompt is unavailable
  if (isInstalled || !deferredPrompt || isDismissed) {
    return null;
  }

  return (
    <>
      <Dialog
        opened={opened}
        withCloseButton={false}
        onClose={close}
        size="lg"
        radius="md"
        position={{ top: "sm", right: "sm" }}
      >
        <Text size="sm" mb="xs" fw={500}>
          Do you want to install PhotoFuse to your device?
        </Text>

        <Group align="flex-end">
          <Button variant="default" onClick={handleNoThanksClick}>
            No, thanks
          </Button>
          <Button onClick={handleInstallClick}>Install</Button>
        </Group>
      </Dialog>
    </>
  );
}

export default PWAInstaller;
